// Camera: pan + zoom over world space, driven by mouse and touch alike
// (touch-first, per MASTER-PLAN.md §2.5).

import { clamp } from '../core/math';
import type { Point } from './iso';

export const MIN_ZOOM = 0.4;
export const MAX_ZOOM = 2.5;

export class Camera {
  /** World-space point shown at the centre of the viewport. */
  x = 0;
  y = 0;
  zoom = 1;
  viewWidth = 0;
  viewHeight = 0;

  resize(width: number, height: number): void {
    this.viewWidth = width;
    this.viewHeight = height;
  }

  panBy(screenDx: number, screenDy: number): void {
    this.x -= screenDx / this.zoom;
    this.y -= screenDy / this.zoom;
  }

  centerOn(worldX: number, worldY: number): void {
    this.x = worldX;
    this.y = worldY;
  }

  /** Absolute zoom, clamped to the same range as pinch and wheel. */
  setZoom(zoom: number): void {
    this.zoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
  }

  /** Zooms while keeping the world point under `screenAnchor` stationary. */
  zoomBy(factor: number, screenAnchor?: Point): void {
    const before = screenAnchor ? this.screenToWorld(screenAnchor.x, screenAnchor.y) : null;
    this.zoom = clamp(this.zoom * factor, MIN_ZOOM, MAX_ZOOM);
    if (before && screenAnchor) {
      const after = this.screenToWorld(screenAnchor.x, screenAnchor.y);
      this.x += before.x - after.x;
      this.y += before.y - after.y;
    }
  }

  worldToScreen(wx: number, wy: number): Point {
    return {
      x: (wx - this.x) * this.zoom + this.viewWidth / 2,
      y: (wy - this.y) * this.zoom + this.viewHeight / 2,
    };
  }

  screenToWorld(sx: number, sy: number): Point {
    return {
      x: (sx - this.viewWidth / 2) / this.zoom + this.x,
      y: (sy - this.viewHeight / 2) / this.zoom + this.y,
    };
  }

  /** Transform applied to the Pixi world container each frame. */
  getTransform(): { x: number; y: number; scale: number } {
    return {
      x: this.viewWidth / 2 - this.x * this.zoom,
      y: this.viewHeight / 2 - this.y * this.zoom,
      scale: this.zoom,
    };
  }
}
